import { TestSummary, TestCase, parseTestCaseDetails } from '../components/TestAutomation/TestExecution/const/TestSummary';
import testExecutionService, { TestCaseResponse } from '../api/testExecutionService';
import { convertArrayToCSV } from './csvExporter';

/**
 * Fetches all test cases for a test execution by paginating through all pages
 * @param testExecutionId - The test execution ID
 * @returns Promise resolving to array of all test cases
 */
export async function fetchAllTestCases(testExecutionId: string): Promise<TestCase[]> {
  const allTestCases: TestCase[] = [];
  let offset = 0;
  const limit = 1000;
  let hasMore = true;

  while (hasMore) {
    try {
      const response = await testExecutionService.getTestCases(testExecutionId, {
        limit,
        offset,
        sortBy: 'createdAt',
        sortOrder: 'ASC'
      });

      // Transform API response to TestCase format
      const transformedCases: TestCase[] = response.data.map((item: TestCaseResponse) => {
        const detailsArray = parseTestCaseDetails(item.details);
        return {
          test_case_id: item.test_case_id,
          test_case_name: item.test_case_name,
          details: detailsArray.length > 0 ? JSON.stringify(detailsArray) : '[]',
          time_taken: item.time_taken, // Keep original format for CSV
          result: item.result as TestCase['result'],
          log_url: item.log_url
        };
      });

      allTestCases.push(...transformedCases);

      // Check if there are more pages
      if (response.nextPage === 0 || response.data.length < limit) {
        hasMore = false;
      } else {
        offset = response.nextPage;
      }
    } catch (error) {
      console.error('Error fetching test cases:', error);
      throw error;
    }
  }

  return allTestCases;
}

/**
 * Formats test case details array for CSV display
 * Converts JSON array string to a readable format (semicolon-separated)
 * @param detailsJsonString - JSON string array, e.g. '["Message 1", "Message 2"]'
 * @returns Formatted string for CSV, e.g. "Message 1; Message 2"
 */
function formatDetailsForCSV(detailsJsonString: string): string {
  try {
    const detailsArray = parseTestCaseDetails(detailsJsonString);
    if (detailsArray.length === 0) {
      return 'No messages';
    }
    return detailsArray.join('; ');
  } catch (error) {
    console.error(error);
    return 'Error parsing details';
  }
}

/**
 * Generates CSV content for test execution summary
 * @param testSummary - The test execution summary data
 * @param testCases - Array of all test cases
 * @returns CSV formatted string
 */
export function exportTestExecutionSummaryToCSV(testSummary: TestSummary, testCases: TestCase[]): string {
  const rows: (string | number)[][] = [];

  // Header section
  rows.push(['Test Execution Summary']);
  rows.push([]);

  // Metadata section
  rows.push(['Metadata']);
  rows.push(['Test Execution ID', testSummary.test_execution_id]);
  rows.push(['Test Collection Name', testSummary.test_collection_name]);
  rows.push(['Workflow ID', testSummary.workflow_id]);
  rows.push(['Product Version', testSummary.product_version]);
  rows.push(['Product Number', testSummary.product_number]);
  rows.push(['Start Timestamp', testSummary.start_timestamp]);
  rows.push(['Duration', testSummary.duration]);
  rows.push([]);

  // Statistics section
  rows.push(['Statistics']);
  rows.push(['Total Executed', testSummary.statistics.total_executed]);
  rows.push(['Success Count', testSummary.statistics.success_count]);
  rows.push(['Failed Count', testSummary.statistics.failed_count]);
  rows.push(['Skipped Count', testSummary.statistics.skipped_count]);
  rows.push(['Success Rate (%)', testSummary.statistics.success_rate]);
  rows.push(['Failed Rate (%)', testSummary.statistics.failed_rate]);
  rows.push(['Skipped Rate (%)', testSummary.statistics.skipped_rate]);
  rows.push([]);

  // Test Cases section
  rows.push(['Test Cases']);
  rows.push(['Test Case ID', 'Test Case Name', 'Details', 'Time Taken (seconds)', 'Result', 'Log URL']);

  // Add test case rows
  if (testCases.length === 0) {
    rows.push(['No test cases available', '', '', '', '', '']);
  } else {
    testCases.forEach((testCase) => {
      rows.push([
        testCase.test_case_id,
        testCase.test_case_name,
        formatDetailsForCSV(testCase.details),
        testCase.time_taken,
        testCase.result,
        testCase.log_url || ''
      ]);
    });
  }

  // Convert to CSV format
  return convertArrayToCSV(rows, true);
}
